import HeroSection from '../components/landing/HeroSection';
import ComparisonSection from '../components/landing/ComparisonSection';
import StatsBar from '../components/landing/StatsBar';
import HowItWorks from '../components/landing/HowItWorks';
import FeaturesGrid from '../components/landing/FeaturesGrid';
import ForSchoolsSection from '../components/landing/ForSchoolsSection';
import PricingSection from '../components/landing/PricingSection';
import BoardWaitlistSection from '../components/landing/BoardWaitlistSection';
import FAQSection from '../components/landing/FAQSection';
import CTASection from '../components/landing/CTASection';
import Footer from '../components/landing/Footer';
import PageMeta from '../components/seo/PageMeta';
import { SIGNUP_BONUS_AMOUNT } from '../lib/types';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title="Xed21 — AI NCERT Question Paper Generator with Bloom's Taxonomy"
        description={`Generate NCERT-aligned, Bloom's Taxonomy-tagged exam questions in seconds. 6 question types, answer keys, explanations. Pay-as-you-go from ₹5. Get ₹${SIGNUP_BONUS_AMOUNT} free on signup. No subscription.`}
        canonicalPath="/"
        keywords={[
          'NCERT question paper generator',
          'CBSE question paper maker',
          'AI question generator for teachers',
          'Bloom taxonomy questions CBSE',
          'NCERT MCQ generator',
          'CBSE assessment tool',
          'online question paper maker India',
          'NCERT exam question generator',
          'higher order thinking questions CBSE',
          'NEP 2020 assessment tool',
          'question paper generator for schools',
          'CBSE test maker online',
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              name: 'Xed21',
              url: 'https://www.xed21.com',
              logo: 'https://www.xed21.com/Xed21_Logo.png',
              email: 'contact@xed21.com',
              description:
                'AI-powered NCERT and CBSE assessment generator for Indian educators.',
              sameAs: [
                'https://www.linkedin.com/company/xed21',
              ],
            },
            {
              '@type': 'SoftwareApplication',
              name: 'Xed21',
              applicationCategory: 'EducationalApplication',
              operatingSystem: 'Web',
              url: 'https://www.xed21.com',
              description:
                "AI-powered NCERT question paper generator with Bloom's Taxonomy alignment. Generates MCQ, True/False, Fill in the Blanks, Matching, and more for CBSE classrooms.",
              offers: {
                '@type': 'Offer',
                price: '5',
                priceCurrency: 'INR',
                description: 'Pay-as-you-go pricing starting from ₹5 per question',
              },
              featureList: [
                'NCERT/CBSE curriculum alignment',
                "Bloom's Taxonomy question tagging",
                '6 question formats: MCQ, MSQ, True/False, Fill in the Blanks, Inline Choice, Matching',
                'Answer keys and explanations',
                'Assessment bundling with point values',
                'Inline question editing',
              ],
            },
            {
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: 'How does Xed21 generate NCERT-aligned questions?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: "Xed21 uses an AI engine trained to produce questions mapped to the NCERT curriculum. You select the board, grade, subject, topic, question type, difficulty, and Bloom's Taxonomy level. The AI generates pedagogically sound questions with plausible distractors, correct answers, and detailed explanations.",
                  },
                },
                {
                  '@type': 'Question',
                  name: "What is Bloom's Taxonomy and why does it matter for CBSE assessments?",
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: "Bloom's Taxonomy classifies questions by cognitive complexity — from Remembering to Creating. CBSE and NEP 2020 guidelines require higher-order thinking questions. Xed21 lets you select the exact Bloom's level for every question.",
                  },
                },
                {
                  '@type': 'Question',
                  name: 'How much does Xed21 cost?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Xed21 uses pay-as-you-go pricing starting at ₹5 per question. You get ₹50 free wallet credit on signup. No subscription, no annual contract, balance never expires.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'What question types does Xed21 support?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Xed21 supports 6 question formats: MCQ, Multiple Select, True/False, Fill in the Blanks, Inline Choice, and Matching. Each includes answer keys and explanations.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'Can I use Xed21 for ICSE, State Boards, or IB?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Xed21 currently supports NCERT/CBSE. ICSE, IB, IGCSE, and State Board support is coming. Join the board waitlist to get notified.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'Can schools and institutions use Xed21?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes. Schools can onboard teachers with bulk wallet credit and institutional billing. Contact us for school onboarding.',
                  },
                },
              ],
            },
          ],
        }}
      />
      <HeroSection />
      <ComparisonSection />
      <StatsBar />
      <HowItWorks />
      <FeaturesGrid />
      <ForSchoolsSection />
      <PricingSection />
      <BoardWaitlistSection />
      <FAQSection />
      <CTASection />
      <Footer />
    </div>
  );
}
